import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Form, Spinner } from "react-bootstrap";
import {
  Plus, X, Trash2, ArrowLeft, Workflow, AlertTriangle, GitBranch,
  Play, Zap, CornerDownRight, Copy as CopyIcon, Sparkles, Smartphone,
} from "lucide-react";
import apiClient from "../../utils/apiClient";
import { can } from "../../utils/access";
import {
  AppCard, AppCardHeader, AppCardTitle, AppCardContent, AppModal,
  AppButton, AppBadge, EmptyState, ConfirmModal, StatusBadge,
} from "../ui";
import GenerateFlowModal from "./GenerateFlowModal";
import FlowPhonePreview from "./FlowPhonePreview";
import "./flows.scss";

/**
 * FlowsTab — build and manage numbered-menu conversation flows.
 *
 *   LMS:  How can we help?
 *           1. Course Information
 *           2. Fees
 *   User: 1
 *   LMS:  <course menu>
 *
 * The editor is deliberately step-and-option shaped rather than a drag-and-drop
 * canvas: a numbered menu IS a list, and a node graph would add ceremony without
 * expressing anything the list can't. Where a canvas earns its place — seeing
 * that an option points nowhere — a validating dropdown does the same job with
 * no layout to maintain.
 */

const TRIGGER_TYPES = [
  { value: "keyword", label: "Keyword", desc: "Starts when the message contains one of your keywords" },
  { value: "first_message", label: "First message", desc: "Starts on a contact's very first message" },
  { value: "manual", label: "Manual only", desc: "Never starts on its own" },
];

const MAX_OPTIONS = 10;

const emptyOption = () => ({ label: "", next: "", action: "", actionTargetId: "", reply: "" });
const emptyStep = (n) => ({ key: `STEP_${n}`, message: "", options: [] });

const emptyFlow = () => ({
  name: "",
  key: "",
  enabled: false,
  triggerType: "keyword",
  keywords: [],
  startStep: "MAIN",
  steps: [{ key: "MAIN", message: "", options: [] }],
  invalidReplyMessage: "Sorry, I didn't understand that. Please reply with one of the numbers below.",
  expiryMinutes: 60,
  priority: 0,
});

/* Money and dates baked into a step go stale silently: unlike the AI agent's
   knowledge base there is no re-seed job for flows, so a fee written into a
   menu in August is still being quoted in December. Worth one warning at the
   moment the text is generated, when rewording it is cheap. */
const PRICE_RE = /(?:₹|\brs\.?\s|\binr\b|\busd\b|\$)\s?\d|\b\d{1,3}(?:,\d{2,3})+\b/i;
const DATE_RE = /\b(?:jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)[a-z]*\b|\b\d{1,2}[/-]\d{1,2}[/-]\d{2,4}\b/i;

const detectStaleContent = (flow) => {
  const texts = (flow?.steps || []).flatMap((s) => [
    s.message || "",
    ...(s.options || []).map((o) => o.reply || ""),
  ]);
  const hasPrice = texts.some((t) => PRICE_RE.test(t));
  const hasDate = texts.some((t) => DATE_RE.test(t));
  if (!hasPrice && !hasDate) return "";
  const what = hasPrice && hasDate ? "prices and dates" : hasPrice ? "prices" : "dates";
  return `Some messages contain ${what}. Flows are not refreshed when your courses change — `
    + "consider pointing the customer at a person instead of quoting a number.";
};

/* ─── Editor ──────────────────────────────────────────────────────────────── */

const FlowEditor = ({ initial, actions, onCancel, onSaved, showToast, notices = [] }) => {
  const canManage = can("whatsapp.manage");
  const [flow, setFlow] = useState(() => ({ ...emptyFlow(), ...(initial || {}) }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [keywordDraft, setKeywordDraft] = useState("");
  /* "" = run the flow the way a customer would, from the trigger. A step key
     drops the handset straight onto that step, for editing a menu five levels
     in without typing your way down to it every time. */
  const [entryStep, setEntryStep] = useState("");
  const [previewRun, setPreviewRun] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [noticesHidden, setNoticesHidden] = useState(false);
  const [showRefine, setShowRefine] = useState(false);
  const [refineNotices, setRefineNotices] = useState([]);

  /* Notices from the parent (a freshly generated flow) and from a revision made
     in here are the same kind of thing, so they share one banner. */
  const allNotices = [...notices, ...refineNotices];

  /* A revision rewrites the whole flow, so it replaces the editor's working
     copy — nothing is saved until the admin presses Save, exactly as if they
     had typed the changes. */
  const applyRefined = (refined, warnings) => {
    const stale = detectStaleContent(refined);
    setFlow((p) => ({ ...p, ...refined }));
    // A rewrite is a different flow; the handset must not keep replaying the old
    // one from a step that may no longer exist.
    setEntryStep("");
    setPreviewRun((n) => n + 1);
    setRefineNotices([...(warnings || []), ...(stale ? [stale] : [])]);
    setNoticesHidden(false);
    setShowRefine(false);
    setError("");
    showToast?.("Flow rewritten — review it, then save");
  };

  const isEdit = !!initial?._id;
  const actionByKey = useMemo(
    () => Object.fromEntries(actions.map((a) => [a.key, a])), [actions],
  );

  /* Only phone-principal actions can run from a menu — a flow cannot prove the
     sender is a particular admin. Filtering the dropdown means the rule is
     visible while authoring instead of arriving as a save error. */
  const menuActions = useMemo(
    () => actions.filter((a) => a.principal === "phone"), [actions],
  );

  const set = (key) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setFlow((p) => ({ ...p, [key]: v }));
    setError("");
  };

  const stepKeys = flow.steps.map((s) => s.key).filter(Boolean);

  const updateStep = (idx, field, value) => {
    setFlow((p) => {
      const steps = [...p.steps];
      steps[idx] = { ...steps[idx], [field]: value };
      return { ...p, steps };
    });
    setError("");
  };

  const addStep = () => setFlow((p) => ({ ...p, steps: [...p.steps, emptyStep(p.steps.length + 1)] }));

  const removeStep = (idx) => {
    setFlow((p) => {
      const gone = p.steps[idx].key;
      const steps = p.steps.filter((_, i) => i !== idx);
      /* Clear references to the deleted step rather than leaving them dangling —
         the server rejects those, and silently pointing at a ghost is worse
         than an obviously empty dropdown. */
      const cleaned = steps.map((s) => ({
        ...s,
        options: (s.options || []).map((o) => (o.next === gone ? { ...o, next: "" } : o)),
      }));
      return {
        ...p,
        steps: cleaned,
        startStep: p.startStep === gone ? (cleaned[0]?.key || "") : p.startStep,
      };
    });
    // The handset cannot be parked on a step that no longer exists.
    if (entryStep === flow.steps[idx]?.key) { setEntryStep(""); setPreviewRun((n) => n + 1); }
  };

  /* Run the conversation from here — the Play button on a step, and the only
     way into a menu that is several replies deep. */
  const jumpTo = (key) => { setEntryStep(key); setPreviewRun((n) => n + 1); };

  const updateOption = (stepIdx, optIdx, field, value) => {
    setFlow((p) => {
      const steps = [...p.steps];
      const options = [...(steps[stepIdx].options || [])];
      options[optIdx] = { ...options[optIdx], [field]: value };
      if (field === "action" && !actionByKey[value]?.targetModel) options[optIdx].actionTargetId = "";
      steps[stepIdx] = { ...steps[stepIdx], options };
      return { ...p, steps };
    });
    setError("");
  };

  const addOption = (stepIdx) => {
    setFlow((p) => {
      const steps = [...p.steps];
      const options = [...(steps[stepIdx].options || [])];
      if (options.length >= MAX_OPTIONS) return p;
      options.push(emptyOption());
      steps[stepIdx] = { ...steps[stepIdx], options };
      return { ...p, steps };
    });
  };

  const removeOption = (stepIdx, optIdx) => {
    setFlow((p) => {
      const steps = [...p.steps];
      steps[stepIdx] = {
        ...steps[stepIdx],
        options: steps[stepIdx].options.filter((_, i) => i !== optIdx),
      };
      return { ...p, steps };
    });
  };

  const addKeyword = () => {
    const k = keywordDraft.trim();
    if (!k) return;
    setFlow((p) => (p.keywords.includes(k) ? p : { ...p, keywords: [...p.keywords, k] }));
    setKeywordDraft("");
  };

  const save = async () => {
    setSaving(true);
    setError("");
    try {
      const payload = {
        ...flow,
        steps: flow.steps.map((s) => ({
          key: s.key.trim(),
          message: s.message,
          options: (s.options || []).map((o) => ({
            label: o.label,
            next: o.next || null,
            action: o.action || "",
            actionTargetId: o.actionTargetId || null,
            reply: o.reply || "",
          })),
        })),
      };
      if (isEdit) await apiClient.put(`/whatsapp/flows/${initial._id}`, payload);
      else await apiClient.post("/whatsapp/flows", payload);
      showToast?.(isEdit ? "Flow updated" : "Flow created");
      onSaved();
    } catch (err) {
      // Server-side validation is the authority (dangling steps, escalation
      // rules); surface its message verbatim rather than paraphrasing.
      setError(err?.response?.data?.message || err?.message || "Failed to save flow");
    }
    setSaving(false);
  };

  const phone = (variant) => (
    <FlowPhonePreview
      flow={flow}
      actions={actions}
      entryStep={entryStep || null}
      seed={previewRun}
      size={variant}
      onExpand={variant === "compact" ? () => setExpanded(true) : undefined}
    />
  );

  return (
    <AppCard overflowVisible>
      <AppCardHeader>
        <div className="wa-card-header-row">
          <AppCardTitle>
            <AppButton variant="ghost" size="sm" onClick={onCancel} title="Back">
              <ArrowLeft size={16} />
            </AppButton>
            <GitBranch size={18} /> {isEdit ? `Edit: ${initial.name}` : "New conversation flow"}
          </AppCardTitle>
          <div className="wa-inline-actions">
            {canManage && (
              <AppButton
                variant="ghost" size="sm"
                onClick={() => setShowRefine(true)}
                disabled={saving || !flow.steps?.length}
                title="Describe a change and let AI rewrite the flow"
              >
                <Sparkles size={14} /> Improve with AI
              </AppButton>
            )}
            <AppButton variant="outline" size="sm" onClick={onCancel} disabled={saving}>Cancel</AppButton>
            {canManage && (
              <AppButton size="sm" onClick={save} disabled={saving}>
                {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : "Save flow"}
              </AppButton>
            )}
          </div>
        </div>
      </AppCardHeader>

      <AppCardContent>
        {!!allNotices.length && !noticesHidden && (
          <div className="fl-notices">
            <Sparkles size={15} />
            <div className="fl-notices__body">
              <strong>Written by AI — read every message before you turn it on.</strong>
              <ul>{allNotices.map((n, i) => <li key={i}>{n}</li>)}</ul>
            </div>
            <button
              type="button" className="fl-icon-btn"
              onClick={() => setNoticesHidden(true)}
              title="Dismiss"
            >
              <X size={14} />
            </button>
          </div>
        )}

        {error && <div className="fl-error"><AlertTriangle size={15} /> {error}</div>}

        <div className="fl-layout">
          <div className="fl-main">
            {/* ── Settings ───────────────────────────────────────────────── */}
            <section className="fl-section">
              <h6 className="fl-section__title">Settings</h6>
              <div className="fl-grid">
                <label className="fl-field">
                  <span className="fl-label">Flow name</span>
                  <Form.Control size="sm" value={flow.name} onChange={set("name")} placeholder="Main menu" />
                </label>
                <label className="fl-field">
                  <span className="fl-label">Key</span>
                  <Form.Control
                    size="sm"
                    value={flow.key}
                    onChange={(e) => setFlow((p) => ({ ...p, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") }))}
                    placeholder="main_menu"
                    disabled={isEdit}
                  />
                  <span className="fl-hint">
                    {isEdit ? "Cannot be changed — conversations reference it" : "Lowercase, letters/numbers/underscores"}
                  </span>
                </label>
                <label className="fl-field">
                  <span className="fl-label">Starts when</span>
                  <Form.Select size="sm" value={flow.triggerType} onChange={set("triggerType")}>
                    {TRIGGER_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </Form.Select>
                  <span className="fl-hint">
                    {TRIGGER_TYPES.find((t) => t.value === flow.triggerType)?.desc}
                  </span>
                </label>
                <label className="fl-field">
                  <span className="fl-label">First step</span>
                  <Form.Select size="sm" value={flow.startStep} onChange={set("startStep")}>
                    {stepKeys.map((k) => <option key={k} value={k}>{k}</option>)}
                  </Form.Select>
                </label>
                <label className="fl-field">
                  <span className="fl-label">Abandon after (minutes)</span>
                  <Form.Control
                    size="sm" type="number" min={1}
                    value={flow.expiryMinutes}
                    onChange={(e) => setFlow((p) => ({ ...p, expiryMinutes: Number(e.target.value) }))}
                  />
                  <span className="fl-hint">A contact who stops replying starts fresh after this</span>
                </label>
                <label className="fl-field">
                  <span className="fl-label">Priority</span>
                  <Form.Control
                    size="sm" type="number"
                    value={flow.priority}
                    onChange={(e) => setFlow((p) => ({ ...p, priority: Number(e.target.value) }))}
                  />
                  <span className="fl-hint">Higher wins when several flows match</span>
                </label>
              </div>

              {flow.triggerType === "keyword" && (
                <div className="fl-field fl-field--wide">
                  <span className="fl-label">Keywords</span>
                  <div className="fl-chips">
                    {flow.keywords.map((k) => (
                      <span key={k} className="fl-chip">
                        {k}
                        <button type="button" onClick={() => setFlow((p) => ({ ...p, keywords: p.keywords.filter((x) => x !== k) }))}>
                          <X size={11} />
                        </button>
                      </span>
                    ))}
                  </div>
                  <div className="fl-chip-add">
                    <Form.Control
                      size="sm"
                      value={keywordDraft}
                      onChange={(e) => setKeywordDraft(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                      placeholder="hi, hello, menu…"
                    />
                    <AppButton size="sm" variant="outline" onClick={addKeyword}>Add</AppButton>
                  </div>
                  <span className="fl-hint">Matched anywhere in the message, case-insensitively</span>
                </div>
              )}

              <label className="fl-field fl-field--wide">
                <span className="fl-label">If the reply matches nothing</span>
                <Form.Control
                  as="textarea" rows={2} size="sm"
                  value={flow.invalidReplyMessage}
                  onChange={set("invalidReplyMessage")}
                />
                <span className="fl-hint">The menu is re-sent after this, so the options are always in reach</span>
              </label>

              <label className="fl-check">
                <Form.Check
                  type="switch"
                  checked={!!flow.enabled}
                  onChange={(e) => setFlow((p) => ({ ...p, enabled: e.target.checked }))}
                  label="Enabled — live for incoming messages"
                />
              </label>
            </section>

            {/* ── Steps ──────────────────────────────────────────────────── */}
            <section className="fl-section">
              <div className="fl-section__head">
                <h6 className="fl-section__title">Steps</h6>
                <AppButton size="sm" variant="outline" onClick={addStep}>
                  <Plus size={13} /> Add step
                </AppButton>
              </div>

              {flow.steps.map((step, si) => (
                <div key={si} className="fl-step">
                  <div className="fl-step__head">
                    <Form.Control
                      size="sm"
                      className="fl-step__key"
                      value={step.key}
                      onChange={(e) => updateStep(si, "key", e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, "_"))}
                    />
                    {flow.startStep === step.key && <AppBadge variant="secondary" size="sm">first step</AppBadge>}
                    <div className="fl-step__spacer" />
                    <button
                      type="button" className="fl-icon-btn"
                      onClick={() => jumpTo(step.key)}
                      title="Run the preview from this step"
                    >
                      <Play size={14} />
                    </button>
                    {flow.steps.length > 1 && (
                      <button
                        type="button" className="fl-icon-btn fl-icon-btn--danger"
                        onClick={() => removeStep(si)}
                        title="Delete step"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>

                  <Form.Control
                    as="textarea" rows={2} size="sm"
                    className="fl-step__msg"
                    value={step.message}
                    onChange={(e) => updateStep(si, "message", e.target.value)}
                    placeholder="How can we help?"
                  />
                  <span className="fl-hint">
                    Numbered options are appended automatically — don't list them here
                  </span>

                  <div className="fl-options">
                    {(step.options || []).map((opt, oi) => {
                      const def = actionByKey[opt.action];
                      const firstWord = (opt.label || "").trim().toLowerCase().split(/\s+/)[0];
                      /* Prefix matching only accepts a prefix that fits ONE
                         option, so two labels sharing a first word make that
                         word match neither — the contact types "fees" and is
                         told they weren't understood. */
                      const clashes = !!firstWord && (step.options || []).some(
                        (o, i) => i !== oi
                          && (o.label || "").trim().toLowerCase().split(/\s+/)[0] === firstWord,
                      );
                      return (
                        <div key={oi} className="fl-option">
                          <span className="fl-option__num">{oi + 1}</span>
                          <div className="fl-option__fields">
                            <div className="fl-option__row">
                              <Form.Control
                                size="sm"
                                value={opt.label}
                                onChange={(e) => updateOption(si, oi, "label", e.target.value)}
                                placeholder="Option label — e.g. Course Information"
                              />
                              <Form.Select
                                size="sm"
                                value={opt.next || ""}
                                onChange={(e) => updateOption(si, oi, "next", e.target.value)}
                                title="Where this option goes next"
                              >
                                <option value="">— end the flow —</option>
                                {stepKeys.map((k) => <option key={k} value={k}>→ {k}</option>)}
                              </Form.Select>
                            </div>
                            <div className="fl-option__row">
                              <Form.Select
                                size="sm"
                                value={opt.action || ""}
                                onChange={(e) => updateOption(si, oi, "action", e.target.value)}
                                title="Action to run when chosen"
                              >
                                <option value="">— no action —</option>
                                {menuActions.map((a) => <option key={a.key} value={a.key}>{a.label}</option>)}
                              </Form.Select>
                              {def?.targetModel && (
                                <Form.Control
                                  size="sm"
                                  value={opt.actionTargetId || ""}
                                  onChange={(e) => updateOption(si, oi, "actionTargetId", e.target.value)}
                                  placeholder={`${def.targetModel} ID`}
                                />
                              )}
                            </div>
                            {/* The reply is sent verbatim the moment this option
                                is chosen, and on an option that ends the flow it
                                is the ONLY thing sent — so it belongs in the
                                editor, not just in whatever the AI wrote. */}
                            <Form.Control
                              as="textarea"
                              rows={1}
                              size="sm"
                              className="fl-option__reply"
                              value={opt.reply || ""}
                              onChange={(e) => updateOption(si, oi, "reply", e.target.value)}
                              placeholder={opt.next
                                ? "Reply sent before moving on (optional)"
                                : "Reply sent when this is chosen — the last thing the contact hears"}
                            />
                            {opt.next === step.key && (
                              <span className="fl-warn">
                                <AlertTriangle size={11} /> Points back at its own step — the contact will loop
                              </span>
                            )}
                            {/* Only once the option is real — a row the admin
                                just added is empty by definition, not broken. */}
                            {!!String(opt.label || "").trim() && !opt.next
                              && !String(opt.reply || "").trim() && (
                              <span className="fl-warn">
                                <AlertTriangle size={11} /> Ends the flow with nothing to send — the contact
                                taps this and gets silence
                              </span>
                            )}
                            {clashes && (
                              <span className="fl-warn">
                                <AlertTriangle size={11} /> Another option starts with “{firstWord}” — typing
                                that word will match neither. Only the numbers will work.
                              </span>
                            )}
                          </div>
                          <button
                            type="button" className="fl-icon-btn fl-icon-btn--danger"
                            onClick={() => removeOption(si, oi)}
                            title="Remove option"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      );
                    })}

                    {(step.options || []).length < MAX_OPTIONS ? (
                      <button type="button" className="fl-add-opt" onClick={() => addOption(si)}>
                        <Plus size={13} /> Add option
                      </button>
                    ) : (
                      <span className="fl-hint">Maximum {MAX_OPTIONS} options — a longer numbered menu stops being readable</span>
                    )}

                    {!(step.options || []).length && (
                      <span className="fl-hint">
                        <CornerDownRight size={11} /> No options — this step ends the flow after it sends
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </section>
          </div>

          {/* ── Preview ────────────────────────────────────────────────── */}
          <aside className="fl-preview">
            <div className="fl-preview__head">
              <h6 className="fl-section__title">Live preview</h6>
              <span className="fl-hint">Nothing is sent</span>
            </div>
            <Form.Select
              size="sm"
              value={entryStep}
              onChange={(e) => { setEntryStep(e.target.value); setPreviewRun((n) => n + 1); }}
              className="mb-2"
              title="Where the preview conversation starts"
            >
              <option value="">Start like a customer</option>
              {stepKeys.map((k) => <option key={k} value={k}>Jump to {k}</option>)}
            </Form.Select>

            {phone("compact")}
          </aside>
        </div>
      </AppCardContent>

      {/* Same simulator, room to read it. The editor column is 300px because the
          form needs the rest; a menu with ten options deserves the whole screen
          when someone is actually walking it. */}
      <AppModal
        show={expanded}
        onHide={() => setExpanded(false)}
        title={`Preview — ${flow.name || "untitled flow"}`}
        icon={<Smartphone size={17} />}
        size="sm"
        bodyClassName="fl-preview-modal"
      >
        {phone("full")}
      </AppModal>

      {showRefine && (
        <GenerateFlowModal
          existingFlow={flow}
          onClose={() => setShowRefine(false)}
          onGenerated={applyRefined}
        />
      )}
    </AppCard>
  );
};

/* ─── Tab ─────────────────────────────────────────────────────────────────── */

const FlowsTab = ({ showToast }) => {
  const canManage = can("whatsapp.manage");
  const [flows, setFlows] = useState([]);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // flow object or 'new'
  const [editorNotices, setEditorNotices] = useState([]);
  const [showGenerate, setShowGenerate] = useState(false);
  const [previewing, setPreviewing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [flowRes, actionRes] = await Promise.all([
        apiClient.get("/whatsapp/flows"),
        apiClient.get("/whatsapp/actions"),
      ]);
      setFlows(flowRes.data.flows || []);
      setActions(actionRes.data.actions || []);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to load flows", "error");
    }
    setLoading(false);
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const toggleEnabled = async (flow) => {
    setBusy(true);
    try {
      await apiClient.put(`/whatsapp/flows/${flow._id}`, { enabled: !flow.enabled });
      setFlows((p) => p.map((f) => (f._id === flow._id ? { ...f, enabled: !f.enabled } : f)));
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to update flow", "error");
    }
    setBusy(false);
  };

  const doDelete = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/whatsapp/flows/${confirmDelete._id}`);
      setFlows((p) => p.filter((f) => f._id !== confirmDelete._id));
      showToast?.("Flow deleted");
      setConfirmDelete(null);
    } catch (err) {
      showToast?.(err?.response?.data?.message || "Failed to delete flow", "error");
    }
    setBusy(false);
  };

  const duplicate = (flow) => {
    // Strips ids so the editor treats it as new; the key must be unique so it
    // gets a suffix the author can then edit.
    const { _id, createdAt, updatedAt, __v, ...rest } = flow;
    setEditorNotices([]);
    setEditing({ ...rest, name: `${flow.name} (copy)`, key: `${flow.key}_copy`, enabled: false });
  };

  const startEditing = (target) => { setEditorNotices([]); setEditing(target); };

  /* A generated flow is unsaved and disabled — it opens in the ordinary editor
     so it goes through the same review, validation and Save as a hand-built
     one. Repairs the server made, plus anything that will go stale, ride along
     as notices above the form. */
  const applyGenerated = (flow, warnings) => {
    const stale = detectStaleContent(flow);
    setEditorNotices([...(warnings || []), ...(stale ? [stale] : [])]);
    setShowGenerate(false);
    setEditing({ ...flow, enabled: false });
    showToast?.("Flow generated — review it, then save");
  };

  if (editing) {
    return (
      <FlowEditor
        initial={editing === "new" ? null : editing}
        actions={actions}
        notices={editorNotices}
        onCancel={() => { setEditing(null); setEditorNotices([]); }}
        onSaved={() => { setEditing(null); setEditorNotices([]); load(); }}
        showToast={showToast}
      />
    );
  }

  return (
    <>
      <AppCard>
        <AppCardHeader>
          <div className="wa-card-header-row">
            <AppCardTitle><Workflow size={18} /> Conversation Flows</AppCardTitle>
            {canManage && (
              <div className="wa-inline-actions">
                <AppButton size="sm" variant="outline" onClick={() => setShowGenerate(true)}>
                  <Sparkles size={14} /> Generate with AI
                </AppButton>
                <AppButton size="sm" onClick={() => startEditing("new")}>
                  <Plus size={14} /> New flow
                </AppButton>
              </div>
            )}
          </div>
        </AppCardHeader>
        <AppCardContent>
          <p className="fl-intro">
            Scripted numbered menus that reply automatically. A flow answers before
            the AI agent does, so use one wherever the answer should be exact rather
            than generated. <strong>Generate with AI</strong> drafts one from your own
            courses, batches and knowledge base — you review and save it yourself.
          </p>

          {loading ? (
            <div className="fl-loading"><Spinner animation="border" size="sm" /> Loading…</div>
          ) : flows.length === 0 ? (
            <EmptyState
              icon={Workflow}
              title="No flows yet"
              description="Create a menu that replies to “hi” with your course, fees and demo options — or let AI draft one from your courses."
              action={canManage && (
                <div className="wa-inline-actions">
                  <AppButton size="sm" onClick={() => setShowGenerate(true)}>
                    <Sparkles size={14} /> Generate with AI
                  </AppButton>
                  <AppButton size="sm" variant="outline" onClick={() => startEditing("new")}>
                    <Plus size={14} /> New flow
                  </AppButton>
                </div>
              )}
            />
          ) : (
            <div className="fl-table-wrap">
              <table className="wa-table">
                <thead>
                  <tr>
                    <th>Name</th><th>Key</th><th>Trigger</th><th>Steps</th>
                    <th>Status</th><th className="wa-table__actions">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {flows.map((f) => (
                    /* The row opens the flow. Editing is what an admin comes to
                       this table to do, and hunting for a 13px icon to do the
                       obvious thing is the kind of friction that makes a list
                       feel like a form. The icons stay for the rest. */
                    <tr
                      key={f._id}
                      className="fl-row"
                      role="button"
                      tabIndex={0}
                      title={`Open ${f.name}`}
                      onClick={() => startEditing(f)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          startEditing(f);
                        }
                      }}
                    >
                      <td>{f.name}</td>
                      <td className="wa-table__mono">{f.key}</td>
                      <td>
                        {f.triggerType === "keyword"
                          ? <span className="fl-kw">{(f.keywords || []).join(", ") || "—"}</span>
                          : <AppBadge variant="secondary" size="sm">{f.triggerType.replace("_", " ")}</AppBadge>}
                      </td>
                      <td>{(f.steps || []).length}</td>
                      <td>
                        {/* active/inactive are the variants StatusBadge styles;
                            the label carries the flow-specific wording. */}
                        <StatusBadge status={f.enabled ? "active" : "inactive"}>
                          {f.enabled ? "Live" : "Off"}
                        </StatusBadge>
                      </td>
                      {/* Every button here does something other than open the
                          flow, so none of them may fall through to the row. */}
                      <td className="wa-table__actions" onClick={(e) => e.stopPropagation()}>
                        {canManage && (
                          <AppButton
                            variant="ghost" size="sm" disabled={busy}
                            onClick={() => toggleEnabled(f)}
                            title={f.enabled ? "Turn off" : "Turn on"}
                          >
                            <Zap size={13} />
                          </AppButton>
                        )}
                        <AppButton
                          variant="ghost" size="sm"
                          onClick={() => setPreviewing(f)}
                          title="Try it on a phone"
                        >
                          <Smartphone size={13} />
                        </AppButton>
                        {canManage && (
                          <>
                            <AppButton variant="ghost" size="sm" onClick={() => startEditing(f)} title="Edit">
                              <GitBranch size={13} />
                            </AppButton>
                            <AppButton variant="ghost" size="sm" onClick={() => duplicate(f)} title="Duplicate">
                              <CopyIcon size={13} />
                            </AppButton>
                            <AppButton
                              variant="ghost" size="sm"
                              onClick={() => setConfirmDelete(f)}
                              title="Delete"
                            >
                              <Trash2 size={13} />
                            </AppButton>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </AppCardContent>
      </AppCard>

      {showGenerate && (
        <GenerateFlowModal
          onClose={() => setShowGenerate(false)}
          onGenerated={applyGenerated}
        />
      )}

      {/* Rendered only while open, so every visit starts the conversation from
          the top rather than resuming whatever the last one left behind. */}
      {previewing && (
        <AppModal
          show
          onHide={() => setPreviewing(null)}
          title={`Preview — ${previewing.name}`}
          icon={<Smartphone size={17} />}
          size="sm"
          bodyClassName="fl-preview-modal"
        >
          <FlowPhonePreview flow={previewing} actions={actions} size="full" />
        </AppModal>
      )}

      <ConfirmModal
        show={!!confirmDelete}
        onHide={() => setConfirmDelete(null)}
        onConfirm={doDelete}
        title="Delete flow"
        message={`Delete "${confirmDelete?.name}"? Contacts currently inside it will simply stop receiving menu replies.`}
        confirmText="Delete"
        variant="danger"
        loading={busy}
      />
    </>
  );
};

export default FlowsTab;
